import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  PaymentHealthHistoryResponse,
  PaymentHealthStatusResponse,
} from "../../interfaces/paymentHealth.interface";
import {
  getPaymentHealthHistory,
  getPaymentHealthStatus,
  markIncidentFalsePositive,
  resolveIncident,
} from "./paymentHealthApi";

export interface PaymentHealthState {
  status: PaymentHealthStatusResponse | null;
  history: PaymentHealthHistoryResponse | null;
  isLoading: boolean;
  isHistoryLoading: boolean;
  error: string | null;
  actionIncidentId: string | null;
  lastUpdatedKey: string;
}

const initialState: PaymentHealthState = {
  status: null,
  history: null,
  isLoading: false,
  isHistoryLoading: false,
  error: null,
  actionIncidentId: null,
  lastUpdatedKey: "",
};

export const fetchPaymentHealthStatusThunk = createAsyncThunk(
  "paymentHealth/fetchStatus",
  async () => {
    return getPaymentHealthStatus();
  }
);

export const fetchPaymentHealthHistoryThunk = createAsyncThunk(
  "paymentHealth/fetchHistory",
  async (days?: number) => {
    return getPaymentHealthHistory(days);
  }
);

export const resolveIncidentThunk = createAsyncThunk(
  "paymentHealth/resolveIncident",
  async (args: { incidentId: string; note?: string }) => {
    await resolveIncident(args.incidentId, args.note);
    return args.incidentId;
  }
);

export const markIncidentFalsePositiveThunk = createAsyncThunk(
  "paymentHealth/markFalsePositive",
  async (args: { incidentId: string; note?: string }) => {
    await markIncidentFalsePositive(args.incidentId, args.note);
    return args.incidentId;
  }
);

export const paymentHealthSlice = createSlice({
  name: "paymentHealth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentHealthStatusThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPaymentHealthStatusThunk.fulfilled, (state, action) => {
        state.status = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchPaymentHealthStatusThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to load payment health";
      })
      .addCase(fetchPaymentHealthHistoryThunk.pending, (state) => {
        state.isHistoryLoading = true;
      })
      .addCase(fetchPaymentHealthHistoryThunk.fulfilled, (state, action) => {
        state.history = action.payload;
        state.isHistoryLoading = false;
      })
      .addCase(fetchPaymentHealthHistoryThunk.rejected, (state) => {
        state.isHistoryLoading = false;
      })
      .addCase(resolveIncidentThunk.pending, (state, action) => {
        state.actionIncidentId = action.meta.arg.incidentId;
      })
      .addCase(resolveIncidentThunk.fulfilled, (state) => {
        state.actionIncidentId = null;
        state.lastUpdatedKey = Date.now().toString();
      })
      .addCase(resolveIncidentThunk.rejected, (state) => {
        state.actionIncidentId = null;
      })
      .addCase(markIncidentFalsePositiveThunk.pending, (state, action) => {
        state.actionIncidentId = action.meta.arg.incidentId;
      })
      .addCase(markIncidentFalsePositiveThunk.fulfilled, (state) => {
        state.actionIncidentId = null;
        state.lastUpdatedKey = Date.now().toString();
      })
      .addCase(markIncidentFalsePositiveThunk.rejected, (state) => {
        state.actionIncidentId = null;
      });
  },
});

export const selectPaymentHealthStatus = (state: RootState) =>
  state.paymentHealth.status;
export const selectPaymentHealthHistory = (state: RootState) =>
  state.paymentHealth.history;
export const selectPaymentHealthHistoryLoading = (state: RootState) =>
  state.paymentHealth.isHistoryLoading;
export const selectPaymentHealthLoading = (state: RootState) =>
  state.paymentHealth.isLoading;
export const selectPaymentHealthError = (state: RootState) =>
  state.paymentHealth.error;
export const selectPaymentHealthActionIncidentId = (state: RootState) =>
  state.paymentHealth.actionIncidentId;
export const selectPaymentHealthLastUpdatedKey = (state: RootState) =>
  state.paymentHealth.lastUpdatedKey;

export default paymentHealthSlice.reducer;
